import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import api from "@/lib/api";
import { SEO, Reveal, MaskedLines, Marquee, Eyebrow, EASE } from "@/components/motion";
import { MANIFESTO, WHY_POINTS, HOME_STEPS, TRUST_ITEMS } from "@/data/content";

const HERO_IMG =
  "https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?auto=format&fit=crop&w=1400&q=80";

export default function Home() {
  const [services, setServices] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
  }, []);

  return (
    <main data-testid="home-page">
      <SEO
        title="HOMELARA | Professional Cleaning in London Zones 1 & 2"
        description="Home, deep, property and business cleaning across London Zones 1 & 2. We don't just clean your home. We give you time back."
      />

      {/* HERO */}
      <section ref={heroRef} data-testid="hero-section" className="relative overflow-hidden">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 pt-32 sm:pt-40 pb-16 sm:pb-24">
          <div className="grid items-center gap-12 lg:grid-cols-[1.15fr_0.85fr]">
            <div>
              <motion.p
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.15, ease: EASE }}
                className="font-mono text-[11px] sm:text-xs uppercase tracking-[0.3em] text-sage-dark"
              >
                Professional Cleaning &middot; London Zones 1 &amp; 2
              </motion.p>

              <h1 className="mt-6 font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.04] tracking-tight text-ink">
                <MaskedLines
                  delay={0.3}
                  lines={[
                    <>A beautifully clean,</>,
                    <>cared<span className="hl-hyphen" data-testid="hero-hyphen"><span className="sr-only">-</span></span>for home.</>,
                    <>More time for</>,
                    <><em className="italic text-blue">the life you love.</em></>,
                  ]}
                />
              </h1>

              <motion.p
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 1.0, ease: EASE }}
                className="mt-7 max-w-md text-base sm:text-lg leading-relaxed text-body"
                data-testid="hero-subtext"
              >
                Professional cleaning for homes, properties and businesses across London.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 1.15, ease: EASE }}
                className="mt-9 flex flex-wrap items-center gap-4"
              >
                <Link
                  to="/book"
                  data-testid="hero-book-button"
                  className="inline-flex items-center gap-2 rounded-full bg-blue px-8 py-4 font-mono text-xs uppercase tracking-[0.18em] text-canvas transition-all duration-300 hover:bg-blue-hover hover:-translate-y-0.5"
                >
                  Book a Clean <ArrowUpRight size={15} />
                </Link>
                <Link
                  to="/contact"
                  data-testid="hero-contact-button"
                  className="inline-flex items-center gap-2 rounded-full border border-ink/20 px-8 py-4 font-mono text-xs uppercase tracking-[0.18em] text-ink transition-all duration-300 hover:border-blue hover:text-blue hover:-translate-y-0.5"
                >
                  Get in Touch
                </Link>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.2, delay: 0.5, ease: EASE }}
              className="relative"
            >
              <div className="img-frame aspect-[4/5] rounded-t-[999px] rounded-b-3xl overflow-hidden">
                <motion.img
                  src={HERO_IMG}
                  alt="Bright, calm and elegant London home interior professionally cleaned by HOMELARA"
                  style={{ y: imgY, scale: 1.15 }}
                  className="h-full w-full object-cover"
                />
              </div>
              <div className="absolute -bottom-5 -left-5 rounded-2xl bg-surface border border-subtle px-6 py-4 shadow-lg">
                <p className="font-serif text-lg italic text-blue">Your space, cared for.</p>
              </div>
            </motion.div>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 1 }}
          className="hidden sm:flex justify-center pb-8"
        >
          <ArrowDown size={18} className="text-sage-dark animate-bounce" />
        </motion.div>
      </section>

      {/* MARQUEE */}
      <Marquee
        items={[
          "A clean home. More time for you.",
          "More time for what matters.",
          "Your space, cared for.",
          "Less cleaning. More living.",
        ]}
      />

      {/* MANIFESTO */}
      <section data-testid="manifesto-section" className="mx-auto max-w-7xl px-5 sm:px-8 py-24 sm:py-36">
        <Reveal>
          <Eyebrow>Our philosophy</Eyebrow>
          <h2 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-ink">
            More than cleaning.
          </h2>
        </Reveal>

        <div className="mt-16 space-y-20 sm:space-y-28">
          {MANIFESTO.map((ch, i) => (
            <Reveal key={ch.n} delay={0.05}>
              <div
                className={`grid items-center gap-10 lg:grid-cols-2 ${
                  i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""
                }`}
              >
                <div>
                  <span className="font-mono text-xs tracking-[0.3em] text-sage-dark">{ch.n}</span>
                  <h3 className="mt-3 font-serif text-3xl sm:text-4xl tracking-tight text-ink">{ch.title}</h3>
                  <p className="mt-5 max-w-lg text-base sm:text-lg leading-relaxed text-body">{ch.text}</p>
                </div>
                <div className="img-frame aspect-[4/3] rounded-3xl overflow-hidden group">
                  <img
                    src={ch.image}
                    alt={ch.alt}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section data-testid="home-services-section" className="bg-warmcard border-y border-subtle">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-24 sm:py-32">
          <Reveal className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <Eyebrow>What we do</Eyebrow>
              <h2 className="mt-4 font-serif text-4xl sm:text-5xl tracking-tight text-ink">
                Thoughtful cleaning, four ways.
              </h2>
            </div>
            <Link
              to="/services"
              data-testid="home-services-viewall"
              className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.18em] text-blue hover:gap-3 transition-all"
            >
              All services <ArrowUpRight size={15} />
            </Link>
          </Reveal>

          <div className="mt-14 grid gap-6 sm:grid-cols-2">
            {services.map((s, i) => (
              <Reveal key={s.slug} delay={i * 0.08}>
                <Link
                  to={`/services/${s.slug}`}
                  data-testid={`service-card-${s.slug}`}
                  className="group block overflow-hidden rounded-3xl border border-subtle bg-surface transition-all duration-500 hover:shadow-xl hover:border-sage"
                >
                  <div className="img-frame aspect-[16/10] overflow-hidden">
                    <img
                      src={s.image}
                      alt={`${s.name} - professional cleaning service in London by HOMELARA`}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-7 sm:p-9">
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-sage-dark">
                        0{i + 1}
                      </span>
                      <span className="flex h-10 w-10 items-center justify-center rounded-full border border-subtle text-blue transition-all duration-300 group-hover:bg-blue group-hover:text-canvas">
                        <ArrowUpRight size={16} />
                      </span>
                    </div>
                    <h3 className="mt-4 font-serif text-2xl sm:text-3xl tracking-tight text-ink">{s.name}</h3>
                    <p className="mt-3 text-sm sm:text-base leading-relaxed text-body">{s.short_desc}</p>
                    <span className="mt-5 inline-block font-mono text-[11px] uppercase tracking-[0.18em] text-blue">
                      View details
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY HOMELARA */}
      <section data-testid="why-section" className="bg-blue-midnight text-canvas">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-24 sm:py-32">
          <Reveal>
            <Eyebrow className="text-sage">Why Homelara?</Eyebrow>
            <h2 className="mt-4 max-w-2xl font-serif text-4xl sm:text-5xl tracking-tight">
              We sell the feeling, <em className="italic text-sage">not just the service.</em>
            </h2>
          </Reveal>
          <div className="mt-16 grid gap-px overflow-hidden rounded-3xl border border-blue-border bg-blue-border sm:grid-cols-2 lg:grid-cols-4">
            {WHY_POINTS.map((p, i) => (
              <Reveal key={p.n} delay={i * 0.08} className="bg-blue-card">
                <div className="h-full p-8 sm:p-10">
                  <span className="font-mono text-xs tracking-[0.3em] text-sage">{p.n}</span>
                  <h3 className="mt-4 font-serif text-2xl tracking-tight">{p.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-canvas/70">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section data-testid="home-how-section" className="mx-auto max-w-7xl px-5 sm:px-8 py-24 sm:py-32">
        <Reveal>
          <Eyebrow>How it works</Eyebrow>
          <h2 className="mt-4 font-serif text-4xl sm:text-5xl tracking-tight text-ink">
            Effortless, from start to finish.
          </h2>
        </Reveal>
        <div className="mt-16 grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
          {HOME_STEPS.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.08}>
              <div className="border-t-2 border-blue pt-6">
                <span className="font-serif text-6xl text-sage/60">{s.n}</span>
                <h3 className="mt-4 font-sans text-lg font-semibold text-blue">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-body">{s.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2} className="mt-14">
          <Link
            to="/how-it-works"
            data-testid="home-how-link"
            className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.18em] text-blue hover:gap-3 transition-all"
          >
            See the full journey <ArrowUpRight size={15} />
          </Link>
        </Reveal>
      </section>

      {/* TESTIMONIALS */}
      <section data-testid="testimonials-section" className="bg-sage-light/60 border-y border-subtle">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-24 sm:py-32">
          <Reveal>
            <Eyebrow>Kind words</Eyebrow>
            <h2 className="mt-4 font-serif text-4xl sm:text-5xl tracking-tight text-ink">
              The feeling, in their words.
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {testimonials.map((t, i) => (
              <Reveal key={t.id || i} delay={i * 0.07}>
                <figure
                  data-testid={`testimonial-card-${i}`}
                  className="flex h-full flex-col rounded-3xl border border-subtle bg-surface p-7"
                >
                  <blockquote className="flex-1 font-serif text-lg leading-relaxed text-ink">
                    &ldquo;{t.text}&rdquo;
                  </blockquote>
                  <figcaption className="mt-6">
                    <p className="text-sm font-semibold text-blue">{t.name}</p>
                    <p className="text-xs text-faint">{t.location}, London</p>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section data-testid="trust-section" className="mx-auto max-w-7xl px-5 sm:px-8 py-20">
        <Reveal>
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-4">
            {TRUST_ITEMS.map((t) => (
              <span key={t} className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.2em] text-faint">
                <span className="h-1.5 w-1.5 rotate-45 bg-sage" />
                {t}
              </span>
            ))}
          </div>
        </Reveal>
      </section>

      {/* FINAL CTA */}
      <section data-testid="final-cta-section" className="bg-blue text-canvas">
        <div className="mx-auto max-w-4xl px-5 sm:px-8 py-24 sm:py-32 text-center">
          <Reveal>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-tight">
              Ready to have one less thing to worry about?
            </h2>
            <p className="mx-auto mt-6 max-w-xl text-base sm:text-lg text-canvas/75">
              Tell us what you need and we'll help you find the right cleaning service for your space.
            </p>
            <Link
              to="/book"
              data-testid="final-cta-book-button"
              className="mt-10 inline-flex items-center gap-2 rounded-full bg-canvas px-10 py-4 font-mono text-xs uppercase tracking-[0.18em] text-blue transition-all duration-300 hover:-translate-y-0.5 hover:shadow-2xl"
            >
              Book a Clean <ArrowUpRight size={15} />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
